import React, { useCallback, useEffect, useState } from 'react';
import { fetchBundleFloorLevels } from '../../services/bundleFloorAPI';
import { getApiErrorMessage } from '../../services/errorUtils';
import type { BundleFloorLevel } from '../../schemas/apiResponse.schemas';
import BundleAllowlistCard from './BundleAllowlistCard';
import BundleFloorQtyPanel from './BundleFloorQtyPanel';
import '../labels/PrintBoxLabel.css';
import './BundleScanSubsetManage.css';

const BundleFloorManage: React.FC = () => {
  const [entries, setEntries] = useState<BundleFloorLevel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadEntries = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setEntries(await fetchBundleFloorLevels());
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to load bundle inventory thresholds'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadEntries();
  }, [loadEntries]);

  const handleEntryUpdated = useCallback((updated: BundleFloorLevel): void => {
    setEntries((prev) =>
      prev.map((entry) =>
        entry.sku === updated.sku
          ? { ...updated, components: entry.components ?? updated.components }
          : entry
      )
    );
  }, []);

  const handleQtyError = useCallback((message: string): void => {
    setError(message);
  }, []);

  return (
    <div className="pbl-container bssm-container">
      <p className="bssm-help">
        Every bundle parent with a pick bin appears here. Edit Expected and per-bin
        quantities. This list is separate from bundle parent scan exceptions.
      </p>

      {error && <div className="bssm-banner bssm-banner--error">{error}</div>}

      <div className="pbl-section">
        <div className="pbl-section-title pbl-section-info">
          <span className="pbl-section-text">
            Bundle parents with pick bins{loading ? '' : ` (${entries.length})`}
          </span>
        </div>
        {loading ? (
          <div className="pbl-message">Loading...</div>
        ) : entries.length === 0 ? (
          <div className="pbl-message">No bundle parents have a pick bin indexed.</div>
        ) : (
          <ul className="bssm-list">
            {entries
              .slice()
              .sort((left, right) => left.sku.localeCompare(right.sku))
              .map((entry) => (
              <BundleAllowlistCard
                key={entry.sku}
                parentSku={entry.sku}
                parentName={entry.itemName}
                components={entry.components ?? []}
              >
                <BundleFloorQtyPanel
                  entry={entry}
                  onUpdated={handleEntryUpdated}
                  onError={handleQtyError}
                />
              </BundleAllowlistCard>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default BundleFloorManage;
